import { Dropdown, DropdownProps } from 'antd';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface BaseDropdownProps extends DropdownProps {
  children: React.ReactNode;
  blockScroll?: boolean; // Whether to block scroll when open
  preventClickThrough?: boolean; // Whether to prevent clicks from propagating
  /**
   * Opt-in: open/close the dropdown instantly with no slide/fade animation.
   * Off by default so the ~30 existing consumers keep their current behavior;
   * enabled only on the property-field / display / quick-filter dropdowns where
   * rapid editing makes the animation feel sluggish (task #2244).
   */
  disableTransition?: boolean;
}

/**
 * Enhanced Dropdown wrapper that:
 * 1. Prevents click-through to underlying elements when closing
 * 2. Optionally blocks scrolling when open
 * 3. Provides consistent z-index management
 * 4. Optionally disables open/close animation (opt-in via `disableTransition`)
 *
 * This is a drop-in replacement for Ant Design's Dropdown.
 * Just replace <Dropdown> with <BaseDropdown> and it works the same way.
 */
export const BaseDropdown = ({
  children,
  open,
  onOpenChange,
  blockScroll = true,
  preventClickThrough = true,
  disableTransition = false,
  ...dropdownProps
}: BaseDropdownProps) => {
  // Block scroll when dropdown is open
  useEffect(() => {
    if (blockScroll && open) {
      // Find the nearest scrollable parent (table body or main container)
      const scrollableParent =
        document.querySelector('.ant-table-body') ||
        document.querySelector('[data-scrollable="true"]') ||
        document.body;

      const originalOverflow = (scrollableParent as HTMLElement).style.overflow;
      (scrollableParent as HTMLElement).style.overflow = 'hidden';

      return () => {
        (scrollableParent as HTMLElement).style.overflow = originalOverflow;
      };
    }
  }, [open, blockScroll]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Closing the dropdown must not forward the click to the element underneath. (bug #2314)
    e.stopPropagation();
    e.preventDefault();
    onOpenChange?.(false, { source: 'trigger' as const });
  };

  return (
    <>
      {/* Portal overlay to prevent click-through — rendered at body level
          so it isn't trapped by ancestor transforms in virtualized rows.
          z-index sits above AntD Modal content (zIndexPopupBase + 10 = 1010)
          but below the dropdown menu so clicks on modal property rows are
          intercepted instead of triggering a sibling dropdown. */}
      {preventClickThrough && open && createPortal(
        <div
          className="fixed inset-0 z-[1040]"
          style={{ background: 'transparent' }}
          onClick={handleOverlayClick}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />,
        document.body
      )}

      <Dropdown
        {...dropdownProps}
        open={open}
        onOpenChange={onOpenChange}
        // Opt-in: empty transition name removes the rc-dropdown motion class so
        // the popup appears/disappears instantly (task #2244). Callers that don't
        // pass `disableTransition` keep AntD's default animation.
        transitionName={disableTransition ? '' : dropdownProps.transitionName}
        // Ensure dropdown renders above the click-prevention overlay
        overlayStyle={{
          zIndex: 1050,
          ...dropdownProps.overlayStyle,
        }}
      >
        {children}
      </Dropdown>
    </>
  );
};

export default BaseDropdown;
